import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import { useDetail } from "seed/gql";
import { Loading } from "seed/helpers"
import { useGet, usePost } from "seed/api";
import { IS_LITE } from "settings";
import { useAuth } from "components/util/helpers/providers/AuthProvider";
import { findFlowsWithUpdates, isSourceSignedOut } from "components/presentation/sync/SyncFlow.lib.source";
import { readAccountFor, removeAccount } from "components/presentation/sync/SyncFlow.lib.session";
import l from "components/util/l";
import View from "./Flows.view";

function Flows({
  currentFlow,
  selectedFolder,
}) {

  const user = useAuth();
  const isDeveloper = user?.isDeveloper;
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // The menu lists projects, not flows: access is granted per environment, so someone
  // invited only to DEV is not a participant of the project's PROD row and asking for
  // PROD alone hid the project from them entirely. The endpoint groups every environment
  // the user reaches onto its project, opens it at the closest one they have, and names
  // it after its PROD flow. The open project comes first, flagged, whichever environment is open.
  const reqFlows = useGet("/flows/get_menu_flows", {
    folder_id: selectedFolder,
    search: searchQuery,
    current_flow_id: currentFlow.id,
  });

  const reqFolder = useDetail(`{
    folder { name }
  }`, selectedFolder);

  const [callClone, reqClone] = usePost("/flows/clone", {
    onCompleted: () => reqFlows.refetch(),
    onError: () => {
      return swal(l("Has llegado a tu límite de soluciones%VG104"),
        l("Actualiza tu plan%VG105"), "error");
    }
  });

  const [isDeletingCurrent, setIsDeletingCurrent] = useState(false);
  const [updatedFlowIds, setUpdatedFlowIds] = useState([]);
  const [updatingFlow, setUpdatingFlow] = useState(null);

  // Lite: brings a synced project's newer definition from its source over this one.
  const [callUpdate, reqUpdate] = usePost("/flows/sync", {
    onCompleted: onUpdated,
    onError: onUpdateError,
    captureErrors: false,
  });

  // Deleting the open project leaves nothing to show, so the app reloads and opens another.
  const [callDelete, reqDelete] = usePost("/flows/delete_flow", {
    onCompleted: () => isDeletingCurrent ? window.location.replace("/app") : reqFlows.refetch()
  })

  const onClickDelete = (flow) => {
    swal({
      title: l("Eliminar solución%SB036"),
      icon: "warning",
      text: `${l("¿Estás seguro que desea eliminar la solución?%SB037")} ${l("Se elimina la solución con todos sus entornos.%EN033")}`,
      buttons: {
        confirm: {
          text: l("Aceptar%GA020"),
          className: "swal-button swal-button--cancel btn-success",
        },
        cancel: l("Cancelar%GA021"),
      },
    }).then((response) => {
      if (response) {
        setIsDeletingCurrent(flow.is_current);
        callDelete({ flow_id: flow.id });
      }
    })
  }

  const onClickClone = (flowId) =>
    callClone({ flow_id: flowId });

  const onClickUpdate = (flow) => {
    swal({
      title: l("¿Sincronizar la solución?%EN042"),
      text: l("Se reemplaza la definición con la del servidor. Los datos se conservan.%EN043"),
      icon: "warning",
      buttons: [l("Cancelar%GA021"), l("Actualizar%AC040")],
    }).then((isConfirmed) => {
      if (!isConfirmed) return;
      setUpdatingFlow(flow);
      callUpdate({
        api_url: flow.sync.api_url,
        token: readAccountFor(flow.sync.api_url).token,
        source_flow_id: flow.sync.flow_id,
        flow_id: flow.id,
      });
    });
  }

  // Declarations, not consts: seed's usePost can call them during the render that runs it,
  // before a const declared below it exists.
  // The open project reloads to run its new definition; any other just leaves the list.
  function onUpdated() {
    if (updatingFlow.is_current) return window.location.reload();
    reqFlows.refetch();
  }

  async function onUpdateError(response) {
    if (await isSourceSignedOut(response)) {
      removeAccount(readAccountFor(updatingFlow.sync.api_url).id);
      return swal(l("La sesión con el servidor expiró, inicia sesión de nuevo%EN045"), "", "error");
    }
    swal(l("No se pudo sincronizar la solución, intenta de nuevo%EN044"), "", "error");
  }

  // The search is answered by the API, so the request waits until the typing stops.
  useEffect(() => {
    const timeout = setTimeout(() => setSearchQuery(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!IS_LITE || !reqFlows.data) return;
    findFlowsWithUpdates(reqFlows.data).then(setUpdatedFlowIds);
  }, [reqFlows.data]);

  // useGet only takes in a new response while it is loading, and only refetch turns that
  // back on: without this the folder changes in the URL and the list stays as it was.
  useEffect(() => {
    reqFlows.refetch()
  }, [selectedFolder, searchQuery, currentFlow.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A refetch keeps the previous list, so only the first load paints the placeholder:
  // swapping the list out while the user types unmounts the search box and steals its focus.
  if ((reqFlows.loading && !reqFlows.loaded) || reqFolder.loading || reqClone.loading || reqDelete.loading || reqUpdate.loading)
    return <PlaceholderView />;

  const flowShown = 9
  const flows = (reqFlows.data ?? []).slice(0, flowShown)
    .map((flow) => ({ ...flow, isUpdateAvailable: updatedFlowIds.includes(flow.id) }))
  const folder = reqFolder.data?.folder ?? null;

  return (
    <View
      folder={folder}
      flows={flows}
      search={search}
      onChangeSearch={setSearch}
      isDeveloper={isDeveloper}
      onClickDelete={onClickDelete}
      onClickClone={onClickClone}
      onClickUpdate={onClickUpdate}
    />
  );
}

const PlaceholderView = () =>
  <div id="start-flows" className="card">
    <div id="start-flows-container" className="card-body mx-1 pb-2">

      <div id="flows" class="w-100">
        <div id="flows-container" class="d-flex flex-wrap justify-content-between">

          <div key="more" className="flow col-12 px-0 mb-2" style={{ minHeight: "14rem" }}>
            <div className="card card-hover-shadow pb-0 px-0 h-100">
              <Loading size={35} />
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

export default Flows;
